'use client';

import { QUERY_KEYS } from '@/@studio-ai/constants';
import { useStudioAI } from '@/@studio-ai/context';
import { studioAIService } from '@/@studio-ai/services';
import { formatRelativeDate } from '@/lib/utils/formatting';
import { Button, Input, Popover, PopoverContent, PopoverTrigger, Spinner } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

const MAX_VISIBLE = 10;

interface ConversationHistoryPopoverProps {
    trigger: React.ReactNode;
    placement?: 'top' | 'bottom' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
    onSelect?: () => void;
}

export function ConversationHistoryPopover({
    trigger,
    placement = 'top',
    onSelect,
}: ConversationHistoryPopoverProps) {
    const { loadConversationMessages, currentConversationId } = useStudioAI();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');

    const queryParams = useMemo(
        () => ({ limit: MAX_VISIBLE, search: search || undefined, page: 1 }),
        [search],
    );

    const { data, isLoading } = useQuery({
        queryKey: QUERY_KEYS.CONVERSATIONS(queryParams),
        queryFn: () => studioAIService.getConversations(queryParams),
        enabled: isOpen,
        staleTime: 1000 * 30,
    });

    const conversations = data?.data ?? [];

    const handleSelect = useCallback(
        (conversationId: string) => {
            loadConversationMessages(conversationId);
            setIsOpen(false);
            setSearch('');
            onSelect?.();
        },
        [loadConversationMessages, onSelect],
    );

    return (
        <Popover
            isOpen={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open);
                if (!open) setSearch('');
            }}
            placement={placement}
            offset={8}
            classNames={{
                content: 'p-0 w-72 max-h-96',
            }}
        >
            <PopoverTrigger>{trigger}</PopoverTrigger>
            <PopoverContent>
                <div className="flex w-full flex-col overflow-hidden">
                    <div className="border-b border-default-200/60 p-2">
                        <Input
                            size="sm"
                            placeholder="Search conversations..."
                            value={search}
                            onValueChange={setSearch}
                            autoFocus
                            startContent={
                                <Icon
                                    icon="lucide:search"
                                    width={14}
                                    className="text-default-400"
                                />
                            }
                            endContent={
                                search ? (
                                    <button
                                        type="button"
                                        onClick={() => setSearch('')}
                                        className="rounded-full p-0.5 text-default-400 hover:text-default-600"
                                        aria-label="Clear search"
                                    >
                                        <Icon icon="lucide:x" width={12} />
                                    </button>
                                ) : null
                            }
                            classNames={{
                                inputWrapper: 'h-8 min-h-8 bg-default-100 border-none shadow-none',
                                input: 'text-tiny',
                            }}
                        />
                    </div>
                    <div className="max-h-72 overflow-y-auto py-1">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-6">
                                <Spinner size="sm" />
                            </div>
                        ) : conversations.length === 0 ? (
                            <div className="px-3 py-4 text-center text-tiny text-default-400">
                                {search ? 'No conversations found' : 'No conversations yet'}
                            </div>
                        ) : (
                            conversations.map((conversation) => (
                                <Button
                                    key={conversation.conversationId}
                                    variant="light"
                                    className={`flex h-auto w-full min-w-0 items-start justify-start gap-2.5 overflow-hidden rounded-none px-3 py-2.5 ${
                                        conversation.conversationId === currentConversationId
                                            ? 'bg-primary-50 dark:bg-primary-50/10'
                                            : ''
                                    }`}
                                    onPress={() => handleSelect(conversation.conversationId)}
                                >
                                    <Icon
                                        icon="lucide:message-square"
                                        width={14}
                                        className="mt-0.5 shrink-0 text-default-400"
                                    />
                                    <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5 overflow-hidden">
                                        <span
                                            className="block w-full truncate text-left text-tiny font-medium text-foreground"
                                            title={conversation.title || 'Untitled'}
                                        >
                                            {conversation.title || 'Untitled'}
                                        </span>
                                        <span className="text-[11px] text-default-400">
                                            {formatRelativeDate(conversation.lastActivityAt)}
                                        </span>
                                    </div>
                                </Button>
                            ))
                        )}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
